import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { serverErrorHandler } from "../services/server-error.service";
import {
  dispatchLogout,
  dispatchSetUser,
  dispatchSetUserNotifications,
} from "../store/dispatcher";
import getLoggedInUser from "../features/profile/api/getLoggedInUser";

/**
 * This hook will fetch user profile data for the logged in user.
 * The data will only be fetched if there is user session token
 * but no user session profile data
 */
export default function useAuth() {
  const [isDone, setIsDone] = useState(false);
  const isFetchingProfile = useRef(false);

  useEffect(() => {
    const auth = getAuth();

    async function clearStaleFirebaseSession() {
      try {
        await signOut(auth);
      } catch {
        // ignore sign-out errors while recovering from a bad session
      }
      dispatchLogout();
    }

    /**
     * Makes request for user data. Data is stored
     * in redux store
     */
    async function fetchUserAsync() {
      if (isFetchingProfile.current) {
        return;
      }
      isFetchingProfile.current = true;

      try {
        const response = await getLoggedInUser();

        dispatchSetUser(response.user);
        dispatchSetUserNotifications({
          notifications: response.notifications.docs,
          totalPages: response.notifications.totalPages,
          totalDocs: response.notifications.totalDocs,
          page: response.notifications.page,
          limit: response.notifications.limit,
          hasNextPage: response.notifications.hasNextPage,
          totalUnread: response.notifications.totalUnread,
          viewNotificationAlert: response.user.viewNotificationAlert,
        });
      } catch (e) {
        if (axios.isAxiosError(e) && e.response?.status === 404) {
          // Firebase session exists but profile route/user record is missing
          await clearStaleFirebaseSession();
          return;
        }

        serverErrorHandler(e, { context: "useAuth → getLoggedInUser" });
      } finally {
        isFetchingProfile.current = false;
        setIsDone(true);
      }
    }

    // listen for firebase user state change
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchUserAsync();
      } else {
        dispatchLogout();
        setIsDone(true);
      }
    });

    return () => unsubscribe();
  }, []);

  return { isDone };
}
